import { forwardRef, useRef, useCallback, type TextareaHTMLAttributes } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { twMerge } from "tailwind-merge";
import { useChatComposer, useChatIsStreaming, useChatSubmit, useChatIsInputDisabled, useChatOnDisabledClick, useChatConcurrentSubmit } from "../ChatContext";
import { useOnStreamEnd } from "../hooks/useOnStreamEnd";

export interface TextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "value" | "onChange"
> {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /**
   * External enabled state (e.g., false when out of credits).
   * @default true
   */
  isEnabled?: boolean;
  /**
   * Auto-focus the textarea when the chat finishes streaming.
   * @default true
   */
  autoFocusOnStreamEnd?: boolean;
  /**
   * Minimum number of rows to display.
   * @default 1
   */
  minRows?: number;
  /**
   * Maximum number of rows before the textarea scrolls internally.
   * @default 6
   */
  maxRows?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, placeholder, value, onChange, onKeyDown, disabled, isEnabled = true, autoFocusOnStreamEnd = true, minRows = 1, maxRows = 6, style, ...props }, ref) => {
    const composer = useChatComposer();
    const isStreaming = useChatIsStreaming();
    const submit = useChatSubmit();
    const chatDisabled = useChatIsInputDisabled();
    const onDisabledClick = useChatOnDisabledClick();
    const concurrentSubmit = useChatConcurrentSubmit();
    const internalRef = useRef<HTMLTextAreaElement>(null);

    // Merge forwarded ref with internal ref
    const mergedRef = useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      },
      [ref],
    );

    useOnStreamEnd(() => {
      if (autoFocusOnStreamEnd) {
        internalRef.current?.focus();
      }
    });

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(e);
      } else {
        composer.setText(e.target.value);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        // Concurrent submit: Enter keeps working while a stream is in
        // flight; the submission lands on the agent inbox at the next
        // beforeModel. Legacy mode (concurrentSubmit=false) gates on
        // !isStreaming as before.
        if (composer.isReady && (concurrentSubmit || !isStreaming) && !chatDisabled) {
          submit();
        }
      }
      onKeyDown?.(e);
    };

    const isDisabledByChat = chatDisabled && onDisabledClick;

    const textarea = (
      <TextareaAutosize
        ref={mergedRef}
        value={value ?? composer.text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={chatDisabled || (disabled ?? !isEnabled)}
        placeholder={placeholder}
        className={twMerge(
          "w-full resize-none border-0 bg-transparent",
          "text-sm placeholder:opacity-50",
          "focus:outline-none focus:ring-0",
          "font-sans",
          className
        )}
        style={style as any}
        data-testid="chat-input"
        minRows={minRows}
        maxRows={maxRows}
        {...props}
      />
    );

    // Disabled elements swallow click events. When the chat is disabled
    // and an onDisabledClick handler exists, wrap with an overlay to capture clicks.
    if (isDisabledByChat) {
      return (
        <div style={{ position: "relative" }} data-testid="chat-input-wrapper">
          {textarea}
          <div
            onClick={onDisabledClick}
            style={{ position: "absolute", inset: 0, cursor: "pointer" }}
            data-testid="chat-input-disabled-overlay"
          />
        </div>
      );
    }

    return textarea;
  }
);

Textarea.displayName = "Chat.Input.Textarea";
